// Selector de variante en matriz: una fila por color y una columna por capacidad. Cada celda trae
// su precio y piezas; la que no existe o no tiene piezas muestra «—» y no se puede elegir.
import { cn } from '@/lib/cn';
import { swatchColorOf } from './productColor';

/** Un SKU de la celda; `tag` distingue dos SKU repetidos del mismo precio. */
export interface MatrixCellOption {
    key: string;
    price: string;
    /** «8 piezas». */
    pieces: string;
    tag: string | null;
    selected: boolean;
    disabled: boolean;
}

export interface MatrixGridColumn {
    key: string;
    label: string;
}

export interface MatrixGridRow {
    key: string;
    /** Nombre del color; también da la muestra. */
    label: string;
    /** Una lista por columna; vacía si la combinación no existe. */
    cells: readonly (readonly MatrixCellOption[])[];
}

const CELL = 'flex min-h-[64px] flex-col items-center justify-center rounded-lg border px-2 py-2';

function EmptyCell({ name }: { name: string }) {
    return (
        <div
            role="gridcell"
            aria-label={`${name}: no disponible`}
            className={cn(CELL, 'border-dashed border-border bg-muted/40 text-muted-foreground')}
        >
            —
        </div>
    );
}

function OptionButton({
    option,
    name,
    onSelect,
}: {
    option: MatrixCellOption;
    name: string;
    onSelect: (key: string) => void;
}) {
    const tag = option.tag === null ? '' : ` · opción ${option.tag}`;
    return (
        <button
            type="button"
            aria-pressed={option.selected}
            aria-label={`${name}${tag}: ${option.price}, ${option.pieces}`}
            onClick={() => {
                onSelect(option.key);
            }}
            className={cn(
                CELL,
                'w-full bg-card transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                option.selected
                    ? 'border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]'
                    : 'border-border hover:border-muted-foreground',
            )}
        >
            {option.tag !== null && (
                <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                    Opción {option.tag}
                </span>
            )}
            <span className="text-[14px] font-bold tabular-nums">{option.price}</span>
            <span className="text-[11.5px] text-muted-foreground tabular-nums">
                {option.pieces}
            </span>
        </button>
    );
}

export function VariantMatrix({
    columns,
    rows,
    onSelect,
    ariaLabel = 'Variantes',
    rowAxisLabel,
}: {
    columns: readonly MatrixGridColumn[];
    rows: readonly MatrixGridRow[];
    onSelect: (key: string) => void;
    /** Nombre de la matriz para el lector de pantalla. */
    ariaLabel?: string;
    /** Encabezado de la columna de filas (p. ej. «Color»); vacío por defecto. */
    rowAxisLabel?: string;
}) {
    const template = {
        gridTemplateColumns: `minmax(96px,auto) repeat(${String(columns.length)}, minmax(110px,1fr))`,
    };
    return (
        <div className="overflow-x-auto">
            <div role="grid" aria-label={ariaLabel} className="grid gap-2" style={template}>
                <div role="row" className="contents">
                    <span
                        role="columnheader"
                        className="self-end pb-1 text-[12px] font-semibold text-muted-foreground"
                    >
                        {rowAxisLabel}
                    </span>
                    {columns.map((column) => (
                        <span
                            key={column.key}
                            role="columnheader"
                            className="self-end pb-1 text-center text-[12px] font-semibold text-muted-foreground"
                        >
                            {column.label}
                        </span>
                    ))}
                </div>
                {rows.map((row) => (
                    <div key={row.key} role="row" className="contents">
                        <span
                            role="rowheader"
                            className="flex min-w-0 items-center gap-2 text-[13px] font-semibold"
                        >
                            <span
                                aria-hidden
                                data-testid="color-dot"
                                className="size-4 flex-none rounded-full border border-border"
                                style={{ backgroundColor: swatchColorOf(row.label) }}
                            />
                            <span className="truncate">{row.label}</span>
                        </span>
                        {row.cells.map((options, index) => {
                            const name = `${row.label} · ${columns[index].label}`;
                            const live = options.filter((option) => !option.disabled);
                            if (live.length === 0) {
                                return <EmptyCell key={columns[index].key} name={name} />;
                            }
                            return (
                                <div
                                    key={columns[index].key}
                                    role="gridcell"
                                    className="flex flex-col gap-1"
                                >
                                    {live.map((option) => (
                                        <OptionButton
                                            key={option.key}
                                            option={option}
                                            name={name}
                                            onSelect={onSelect}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}
